import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { getAllReadings, getAllOutlets, getUtilities, getStatistics, exportAdminReadingsCSV, updateReading, resetReadingImage } from '../api';

function AdminDashboard() {
  const [readings, setReadings] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [utilities, setUtilities] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly' or 'list'
  const [collapsedMonths, setCollapsedMonths] = useState({});
  const [collapsedUtilities, setCollapsedUtilities] = useState({});
  const [editingReading, setEditingReading] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    utilityId: '',
    outletId: ''
  });
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const canEdit = user?.role === 'admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [readingsRes, outletsRes, utilitiesRes, statsRes] = await Promise.all([
        getAllReadings(filters),
        getAllOutlets(),
        getUtilities(),
        getStatistics()
      ]);
      setReadings(readingsRes.data);
      setOutlets(outletsRes.data);
      setUtilities(utilitiesRes.data);
      setStatistics(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleApplyFilters = async () => {
    setLoading(true);
    try {
      const response = await getAllReadings(filters);
      setReadings(response.data);
    } catch (error) {
      console.error('Error applying filters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      utilityId: '',
      outletId: ''
    });
    fetchData();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDownloadCSV = async () => {
    setLoading(true);
    try {
      const res = await exportAdminReadingsCSV(filters);
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `readings-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditReading = (reading) => {
    setEditingReading({
      id: reading.id,
      readingValue: reading.readingValue,
      remark: reading.remark || ''
    });
    setMessage({ type: '', text: '' });
  };

  const handleSaveReading = async () => {
    try {
      await updateReading(editingReading.id, {
        readingValue: editingReading.readingValue,
        remark: editingReading.remark
      });
      setMessage({ type: 'success', text: '✓ Reading updated successfully!' });
      setEditingReading(null);
      handleApplyFilters();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to update reading' 
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingReading(null);
    setMessage({ type: '', text: '' });
  };

  const handleResetImages = async (readingId) => {
    if (!window.confirm('Reset images for this reading? This cannot be undone.')) return;
    setLoading(true);
    try {
      await resetReadingImage(readingId);
      setMessage({ type: 'success', text: '✓ Images reset for this reading' });
      handleApplyFilters();
    } catch (error) {
      console.error('Failed to reset images:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to reset images' });
    } finally {
      setLoading(false);
    }
  };


  const filteredOutlets = filters.utilityId
    ? outlets.filter(o => o.utilityId === parseInt(filters.utilityId))
    : outlets;

  // Group readings by month and utility
  const groupReadingsByMonth = () => {
    const grouped = {};
    readings.forEach(reading => {
      const date = new Date(reading.createdAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          label: monthLabel,
          utilities: {}
        };
      }
      
      const utilityName = reading.Outlet?.Utility?.name || 'Unknown';
      if (!grouped[monthKey].utilities[utilityName]) {
        grouped[monthKey].utilities[utilityName] = [];
      }
      
      grouped[monthKey].utilities[utilityName].push(reading);
    });
    
    // Sort months in descending order (most recent first)
    return Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0]));
  };

  const toggleMonth = (monthKey) => {
    setCollapsedMonths(prev => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  };

  const toggleUtility = (monthKey, utilityName) => {
    const key = `${monthKey}-${utilityName}`;
    setCollapsedUtilities(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const groupedReadings = groupReadingsByMonth();

  if (loading) {
    return <div className="loading">Loading dashboard data...</div>;
  }

  return (
    <div>
      <div className="navbar">
        <h1>MeterBill Pro - Admin</h1>
        <div className="navbar-right">
          <button onClick={() => navigate('/admin/users')} className="btn" style={{ marginRight: 8 }}>Manage Users</button>
          <span className="navbar-user">👤 {user?.username}</span>
          <button onClick={handleLogout}>Logout</button>
        </div>
      </div>
      <div className="container">
        <div className="page-header">
          <h2>Admin Dashboard</h2>
          <p>Monitor all meter readings and outlets</p>
        </div>

        {message.text && (
          <div style={{ 
            marginBottom: 16, 
            padding: 12, 
            borderRadius: 6,
            background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
            color: message.type === 'success' ? '#065f46' : '#991b1b'
          }}>
            {message.text}
          </div>
        )}

        {statistics && (
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Outlets</h3>
              <div className="value">{statistics.totalOutlets}</div>
            </div>
            <div className="stat-card">
              <h3>Total Reading This Months</h3>
              <div className="value">{statistics.totalReadings}</div>
            </div>
            <div className="stat-card">
              <h3>Deactivate Outlet</h3>
              <div className="value">{statistics.totalUsers}</div>
            </div>
          </div>
        )}

        <div className="filter-section">
          <h3>🔍 Filter Readings</h3>
          <div className="filter-group">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group">
              <label>Utility</label>
              <select
                name="utilityId"
                value={filters.utilityId}
                onChange={handleFilterChange}
              >
                <option value="">All Utilities</option>
                {utilities.map((utility) => (
                  <option key={utility.id} value={utility.id}>
                    {utility.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Outlet</label>
              <select
                name="outletId"
                value={filters.outletId}
                onChange={handleFilterChange}
              >
                <option value="">All Outlets</option>
                {filteredOutlets.map((outlet) => (
                  <option key={outlet.id} value={outlet.id}>
                    {outlet.tenantName} - {outlet.location}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={handleApplyFilters} className="btn btn-primary">
              Apply Filters
            </button>
            <button onClick={handleResetFilters} className="btn btn-secondary">
              Reset
            </button>
            <button onClick={handleDownloadCSV} className="btn btn-outline">
              Download CSV
            </button>
          </div>
        </div>

        {/* Create user moved to /admin/users for a cleaner UI */}

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700' }}>📊 All Readings</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                onClick={() => setViewMode('monthly')}
                className={viewMode === 'monthly' ? 'btn btn-primary' : 'btn btn-secondary'}
                style={{ padding: '8px 16px' }}
              >
                Monthly View
              </button>
              <button 
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'btn btn-primary' : 'btn btn-secondary'}
                style={{ padding: '8px 16px' }}
              >
                List View
              </button>
            </div>
          </div>
          
          {readings.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📭</div>
              <h3>No readings found</h3>
              <p>Try adjusting your filters or wait for users to submit readings</p>
            </div>
          ) : viewMode === 'monthly' ? (
            <div>
              {groupedReadings.map(([monthKey, monthData]) => (
                <div key={monthKey} style={{ marginBottom: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                  <div 
                    onClick={() => toggleMonth(monthKey)}
                    style={{ 
                      padding: '16px', 
                      background: '#f9fafb', 
                      cursor: 'pointer', 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: '600',
                      fontSize: '16px'
                    }}
                  >
                    <span>📅 {monthData.label}</span>
                    <span>{collapsedMonths[monthKey] ? '▼' : '▲'}</span>
                  </div>
                  
                  {!collapsedMonths[monthKey] && (
                    <div style={{ padding: '16px' }}>
                      {Object.entries(monthData.utilities).map(([utilityName, utilityReadings]) => (
                        <div key={utilityName} style={{ marginBottom: '16px' }}>
                          <div 
                            onClick={() => toggleUtility(monthKey, utilityName)}
                            style={{ 
                              padding: '12px', 
                              background: '#eff6ff', 
                              cursor: 'pointer',
                              borderRadius: '6px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '8px',
                              fontWeight: '500'
                            }}
                          >
                            <span>
                              <span className="badge badge-primary" style={{ marginRight: '8px' }}>{utilityName}</span>
                              ({utilityReadings.length} readings)
                            </span>
                            <span>{collapsedUtilities[`${monthKey}-${utilityName}`] ? '▼' : '▲'}</span>
                          </div>
                          
                          {!collapsedUtilities[`${monthKey}-${utilityName}`] && (
                            <div className="table-container" style={{ marginTop: '8px' }}>
                              <table className="table">
                                <thead>
                                  <tr>
                                    <th>Date</th>
                                    <th>Tenant</th>
                                    <th>Location</th>
                                    <th>Image</th>
                                    <th>Serial No</th>
                                    <th>Reading</th>
                                    <th>Remark</th>
                                    <th>Submitted By</th>
                                    {canEdit && <th>Actions</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {utilityReadings.map((reading) => (
                                    <tr key={reading.id}>
                                      <td style={{ fontSize: '13px' }}>
                                        {new Date(reading.createdAt).toLocaleDateString()}<br/>
                                        <small style={{ color: '#6b7280' }}>{new Date(reading.createdAt).toLocaleTimeString()}</small>
                                      </td>
                                      <td><strong>{reading.Outlet?.tenantName}</strong></td>
                                      <td>{reading.Outlet?.location}</td>
                                      <td>
                                        {reading.imagePath ? (
                                          (() => {
                                            const imageUrl = reading.imagePath.startsWith('http') ? reading.imagePath : `/${reading.imagePath}`;
                                            return (
                                              <img
                                                src={imageUrl}
                                                alt="meter"
                                                style={{ width: 80, height: 60, objectFit: 'cover', cursor: 'pointer', borderRadius: 4 }}
                                                onClick={() => window.open(imageUrl, '_blank')}
                                              />
                                            );
                                          })()
                                        ) : (
                                          '-'
                                        )}
                                      </td>
                                      <td><span className="badge badge-success">{reading.Outlet?.serialNo}</span></td>
                                      <td>
                                        {editingReading && editingReading.id === reading.id ? (
                                          <input
                                            type="number"
                                            step="0.01"
                                            value={editingReading.readingValue}
                                            onChange={(e) => setEditingReading({...editingReading, readingValue: e.target.value})}
                                            style={{ width: '100px', padding: '4px', fontSize: '14px' }}
                                          />
                                        ) : (
                                          <strong style={{ fontSize: '16px' }}>{reading.readingValue}</strong>
                                        )}
                                      </td>
                                      <td>
                                        {editingReading && editingReading.id === reading.id ? (
                                          <textarea
                                            value={editingReading.remark}
                                            onChange={(e) => setEditingReading({...editingReading, remark: e.target.value})}
                                            rows="2"
                                            style={{ width: '150px', padding: '4px', fontSize: '12px' }}
                                          />
                                        ) : (
                                          reading.remark || '-'
                                        )}
                                      </td>
                                      <td>{reading.User?.username}</td>
                                      {canEdit && (
                                        <td>
                                          {editingReading && editingReading.id === reading.id ? (
                                            <div style={{ display: 'flex', gap: '4px' }}>
                                              <button
                                                onClick={handleSaveReading}
                                                className="btn btn-success"
                                                style={{ padding: '4px 8px', fontSize: '12px' }}
                                              >
                                                Save
                                              </button>
                                              <button
                                                onClick={handleCancelEdit}
                                                className="btn btn-secondary"
                                                style={{ padding: '4px 8px', fontSize: '12px' }}
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                          ) : (
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                              <button
                                                onClick={() => handleEditReading(reading)}
                                                className="btn btn-primary"
                                                style={{ padding: '4px 8px', fontSize: '12px' }}
                                              >
                                                Edit
                                              </button>
                                              <button
                                                onClick={() => handleResetImages(reading.id)}
                                                className="btn btn-warning"
                                                style={{ padding: '4px 8px', fontSize: '12px' }}
                                              >
                                                Reset Images
                                              </button>
                                            </div>
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Utility</th>
                    <th>Tenant</th>
                    <th>Location</th>
                    <th>Image</th>
                    <th>Serial No</th>
                    <th>Reading</th>
                    <th>Remark</th>
                    <th>Submitted By</th>
                    {canEdit && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {readings.map((reading) => (
                    <tr key={reading.id}>
                      <td style={{ fontSize: '13px' }}>
                        {new Date(reading.createdAt).toLocaleDateString()}<br/>
                        <small style={{ color: '#6b7280' }}>{new Date(reading.createdAt).toLocaleTimeString()}</small>
                      </td>
                      <td><span className="badge badge-primary">{reading.Outlet?.Utility?.name}</span></td>
                      <td><strong>{reading.Outlet?.tenantName}</strong></td>
                      <td>{reading.Outlet?.location}</td>
                      <td>
                        {reading.imagePath ? (
                          (() => {
                            const imageUrl = reading.imagePath.startsWith('http') ? reading.imagePath : `/${reading.imagePath}`;
                            return (
                              <img
                                src={imageUrl}
                                alt="meter"
                                style={{ width: 80, height: 60, objectFit: 'cover', cursor: 'pointer', borderRadius: 4 }}
                                onClick={() => window.open(imageUrl, '_blank')}
                              />
                            );
                          })()
                        ) : (
                          '-'
                        )}
                      </td>
                      <td><span className="badge badge-success">{reading.Outlet?.serialNo}</span></td>
                      <td>
                        {editingReading && editingReading.id === reading.id ? (
                          <input
                            type="number"
                            step="0.01"
                            value={editingReading.readingValue}
                            onChange={(e) => setEditingReading({...editingReading, readingValue: e.target.value})}
                            style={{ width: '100px', padding: '4px', fontSize: '14px' }}
                          />
                        ) : (
                          <strong style={{ fontSize: '16px' }}>{reading.readingValue}</strong>
                        )}
                      </td>
                      <td>
                        {editingReading && editingReading.id === reading.id ? (
                          <textarea
                            value={editingReading.remark}
                            onChange={(e) => setEditingReading({...editingReading, remark: e.target.value})}
                            rows="2"
                            style={{ width: '150px', padding: '4px', fontSize: '12px' }}
                          />
                        ) : (
                          reading.remark || '-'
                        )}
                      </td>
                      <td>{reading.User?.username}</td>
                      {canEdit && (
                        <td>
                          {editingReading && editingReading.id === reading.id ? (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                onClick={handleSaveReading}
                                className="btn btn-success"
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="btn btn-secondary"
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                onClick={() => handleEditReading(reading)}
                                className="btn btn-primary"
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                                >
                                Edit
                              </button>
                              <button
                                onClick={() => handleResetImages(reading.id)}
                                className="btn btn-warning"
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                              >
                                Reset Images
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* <div className="card">
          <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '700' }}>🏢 All Outlets</h3>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Utility</th>
                  <th>Tenant Name</th>
                  <th>Location</th>
                  <th>Meter No</th>
                  <th>Serial No</th>
                </tr>
              </thead>
              <tbody>
                {outlets.map((outlet) => (
                  <tr key={outlet.id}>
                    <td><span className="badge badge-primary">{outlet.Utility?.name}</span></td>
                    <td><strong>{outlet.tenantName}</strong></td>
                    <td>{outlet.location}</td>
                    <td>{outlet.meterNo || '-'}</td>
                    <td><span className="badge badge-success">{outlet.serialNo}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div> */}
      </div>
    </div>
  );
}

export default AdminDashboard;
